import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import useWindowSize from '../hooks/useWindowSize';
import axios from 'axios';

export default function Navbar() {
  const { user, logout, token, API } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useWindowSize();
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  useEffect(() => {
    if (!token) return;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/messages/unread/count`, { headers: { Authorization: `Bearer ${token}` } });
        setUnread(res.data.count);
      } catch (err) {}
    };
    const fetchUnreadNotifs = async () => {
      try {
        const res = await axios.get(`${API}/notifications/unread/count`, { headers: { Authorization: `Bearer ${token}` } });
        setUnreadNotifs(res.data.count);
      } catch (err) {}
    };
    fetchUnread();
    fetchUnreadNotifs();
    const interval = setInterval(() => { fetchUnread(); fetchUnreadNotifs(); }, 10000);
    return () => clearInterval(interval);
  }, [token, API]);

  const handleLogout = () => { logout(); navigate('/login'); setMenuOpen(false); };

  const isActive = (path, exact = false) => exact ? location.pathname === path : location.pathname.startsWith(path);

  const linkStyle = (path, exact = false) => ({
    fontSize: '14px', fontWeight: 500, padding: '7px 14px', borderRadius: '20px',
    color: isActive(path, exact) ? 'var(--blue)' : 'var(--text)',
    background: isActive(path, exact) ? 'var(--blue-bg)' : 'transparent',
    transition: 'all .2s', whiteSpace: 'nowrap',
    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '5px',
  });

  // Centre links — always visible
  const centerLinks = [
    { path: '/', label: 'Feed', exact: true },
    { path: '/projects', label: 'Projets' },
    { path: '/search', label: 'Recherche' },
    { path: '/suggestions', label: '💡 Suggestions' },
  ];

  return (
    <>
      <nav style={{
        background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, zIndex: 100,
        height: '64px', padding: isMobile ? '0 16px' : '0 24px',
        display: 'flex', alignItems: 'center', gap: '8px',
      }}>

        {/* Logo */}
        <Link to="/" style={{ fontSize: '20px', fontWeight: 800, color: 'var(--blue)', letterSpacing: '-0.5px', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, textDecoration: 'none' }}>
          Agora
          <span style={{ fontSize: '10px', background: 'var(--blue-bg)', color: 'var(--blue)', padding: '2px 6px', borderRadius: '8px', fontWeight: 600, letterSpacing: '1px' }}>BETA</span>
        </Link>

        {/* Centre — desktop */}
        {!isMobile && (
          <div style={{ flex: 1, display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'center' }}>
            {centerLinks.map(({ path, label, exact }) => (
              <Link key={path} to={path} style={linkStyle(path, exact)}>{label}</Link>
            ))}
            {user && (
              <Link to="/messages" style={linkStyle('/messages')}>
                Messages
                {unread > 0 && <span style={{ background: '#E53E3E', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '1px 5px', borderRadius: '8px', minWidth: '16px', textAlign: 'center' }}>{unread}</span>}
              </Link>
            )}
          </div>
        )}

        {/* Right side — desktop */}
        {!isMobile && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
            {user ? (
              <>
                {user.is_admin && (
                  <Link to="/admin" style={{ ...linkStyle('/admin'), background: isActive('/admin') ? '#FAF5FF' : 'transparent', color: isActive('/admin') ? '#6B46C1' : 'var(--text)' }}>
                    🛡️ Admin
                  </Link>
                )}
                <Link to="/notifications" style={{ ...linkStyle("/notifications"), display: "flex", alignItems: "center", gap: "4px", position: "relative", padding: "7px 12px" }}>
                  🔔
                  {unreadNotifs > 0 && <span style={{ background: "#E53E3E", color: "#fff", fontSize: "10px", fontWeight: 700, padding: "1px 5px", borderRadius: "8px", minWidth: "16px", textAlign: "center" }}>{unreadNotifs}</span>}
                </Link>
                <Link to={`/profile/${user.username}`} style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '5px 12px', borderRadius: '20px', background: isActive(`/profile/${user.username}`) ? 'var(--blue-bg)' : 'transparent', textDecoration: 'none', transition: 'all .2s' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--blue-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '11px', color: 'var(--blue)', flexShrink: 0 }}>
                    {user.username.slice(0, 2).toUpperCase()}
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dark)' }}>{user.username}</span>
                </Link>
                <button onClick={handleLogout} style={{ background: 'transparent', color: 'var(--muted)', border: '1px solid var(--border)', padding: '6px 12px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', transition: 'all .2s', whiteSpace: 'nowrap' }}
                  onMouseEnter={e => { e.target.style.color = 'var(--red)'; e.target.style.borderColor = 'var(--red)'; }}
                  onMouseLeave={e => { e.target.style.color = 'var(--muted)'; e.target.style.borderColor = 'var(--border)'; }}
                >Déco</button>
              </>
            ) : (
              <>
                <Link to="/login" style={{ fontSize: '14px', color: 'var(--text)', fontWeight: 500, padding: '7px 14px', borderRadius: '20px', textDecoration: 'none' }}>Connexion</Link>
                <Link to="/register" style={{ background: 'var(--blue)', color: '#fff', padding: '8px 18px', borderRadius: '24px', fontSize: '13px', fontWeight: 600, boxShadow: '0 4px 12px rgba(43,108,176,0.3)', textDecoration: 'none', whiteSpace: 'nowrap' }}>Rejoindre →</Link>
              </>
            )}
          </div>
        )}

        {/* Mobile burger */}
        {isMobile && (
          <button onClick={() => setMenuOpen(!menuOpen)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '5px', padding: '4px', position: 'relative', flexShrink: 0 }}>
            {unread > 0 && <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#E53E3E', color: '#fff', fontSize: '10px', fontWeight: 700, width: '16px', height: '16px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{unread}</span>}
            <div style={{ width: '22px', height: '2px', background: 'var(--dark)', transition: 'all .3s', transform: menuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none' }}></div>
            <div style={{ width: '22px', height: '2px', background: 'var(--dark)', transition: 'all .3s', opacity: menuOpen ? 0 : 1 }}></div>
            <div style={{ width: '22px', height: '2px', background: 'var(--dark)', transition: 'all .3s', transform: menuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none' }}></div>
          </button>
        )}
      </nav>

      {/* Mobile menu */}
      {isMobile && menuOpen && (
        <div style={{ position: 'fixed', top: '64px', left: 0, right: 0, background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(12px)', borderBottom: '1px solid var(--border)', padding: '16px 20px', zIndex: 99, animation: 'fadeUp .2s ease' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {centerLinks.map(({ path, label, exact }) => (
              <Link key={path} to={path} onClick={() => setMenuOpen(false)} style={{ padding: '12px 16px', borderRadius: '12px', fontSize: '15px', fontWeight: 500, color: isActive(path, exact) ? 'var(--blue)' : 'var(--dark)', background: isActive(path, exact) ? 'var(--blue-bg)' : 'transparent', textDecoration: 'none' }}>{label}</Link>
            ))}
            {user && (
              <>
                <Link to="/messages" onClick={() => setMenuOpen(false)} style={{ padding: '12px 16px', borderRadius: '12px', fontSize: '15px', fontWeight: 500, color: isActive('/messages') ? 'var(--blue)' : 'var(--dark)', background: isActive('/messages') ? 'var(--blue-bg)' : 'transparent', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  Messages
                  {unread > 0 && <span style={{ background: '#E53E3E', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px' }}>{unread}</span>}
                </Link>
                <Link to="/notifications" onClick={() => setMenuOpen(false)} style={{ padding: '12px 16px', borderRadius: '12px', fontSize: '15px', fontWeight: 500, color: isActive('/notifications') ? 'var(--blue)' : 'var(--dark)', background: isActive('/notifications') ? 'var(--blue-bg)' : 'transparent', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  🔔 Notifications
                  {unreadNotifs > 0 && <span style={{ background: '#E53E3E', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px' }}>{unreadNotifs}</span>}
                </Link>
                <Link to={`/profile/${user.username}`} onClick={() => setMenuOpen(false)} style={{ padding: '12px 16px', borderRadius: '12px', fontSize: '15px', fontWeight: 500, color: isActive(`/profile/${user.username}`) ? 'var(--blue)' : 'var(--dark)', background: isActive(`/profile/${user.username}`) ? 'var(--blue-bg)' : 'transparent', textDecoration: 'none' }}>Mon profil</Link>
                {user.is_admin && (
                  <Link to="/admin" onClick={() => setMenuOpen(false)} style={{ padding: '12px 16px', borderRadius: '12px', fontSize: '15px', fontWeight: 500, color: '#6B46C1', background: '#FAF5FF', textDecoration: 'none' }}>🛡️ Admin</Link>
                )}
              </>
            )}
            <div style={{ height: '1px', background: 'var(--border)', margin: '8px 0' }}></div>
            {user ? (
              <button onClick={handleLogout} style={{ padding: '12px 16px', borderRadius: '12px', fontSize: '15px', fontWeight: 500, color: 'var(--red)', background: '#FFF5F5', border: 'none', cursor: 'pointer', textAlign: 'left' }}>Déconnexion</button>
            ) : (
              <>
                <Link to="/login" onClick={() => setMenuOpen(false)} style={{ padding: '12px 16px', borderRadius: '12px', fontSize: '15px', fontWeight: 500, color: 'var(--dark)', textDecoration: 'none' }}>Connexion</Link>
                <Link to="/register" onClick={() => setMenuOpen(false)} style={{ padding: '13px 16px', borderRadius: '12px', fontSize: '15px', fontWeight: 600, color: '#fff', background: 'var(--blue)', textAlign: 'center', marginTop: '4px', boxShadow: '0 4px 12px rgba(43,108,176,0.3)', textDecoration: 'none', display: 'block' }}>Rejoindre →</Link>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
